import { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { SearchService } from 'gw-capability-gateway';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { JobType } from 'wmic-portals-utils-js/StringConstants';
import { ICON_NAMES, CONSTANTS, WMICVariousUtil, WMICRichTextUtil, LOBConstants, WMICAddressDetailsUtil } from 'wmic-pe-portals-utils-js';
import { useTranslator } from '@jutro/locale';
import { withRouter, useHistory } from 'react-router-dom';
import { Tooltip } from '@jutro/components';

import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import metadata from './SearchResults.metadata.json5';

import styles from './SearchResults.module.scss';
import messages from './SearchResults.messages';

import { Icon } from '@jutro/legacy/components';

function SearchResults(props) {
    const {
        match: {
            params: { searchText }
        },
        param,
        title,
        noResultsMessage
    } = props;
    const translator = useTranslator();
    const [searchResult, setSearchResult] = useState({
        accounts: [],
        policies: [],
        submissions: []
    });
    const [prevParam, setPrevParam] = useState('');
    const [loading, setLoading] = useState(false);
    const history = useHistory();

    const { authHeader } = useAuthentication();

    const getPhoneNumber = (accountHolder) => {
        switch (accountHolder.primaryPhoneType) {
        case CONSTANTS.PHONE_TYPES.HOME:
            return accountHolder.homeNumber;
        case CONSTANTS.PHONE_TYPES.MOBILE:
            return accountHolder.cellNumber;
        case CONSTANTS.PHONE_TYPES.WORK:
            return accountHolder.workNumber;
        default:
            return undefined;
        }
    };

    const getAccountIcon = useCallback((account) => {
        if(account.isPerson){
            return "mi-person"
        } 
        
        if (account.isCommercial) {
            return ICON_NAMES.ICON_BUILDING;
        }

        return "";
    }, [])

    const getAddressLine = ({addressLine1, addressLine2}) => `${addressLine1}, ${addressLine2 ? addressLine2 + ',' : ''}`;

    const populateAccountResults = (data) => {
        const accounts = [];

        if (data.submissions.length === 0 && data.policies.length === 0 && data.accounts.length === 1) {
            history.push(`/accounts/${data.accounts[0].accountNumber}/summary`)
        }

        if (data.accounts.length) {
            data.accounts.forEach((account) => {
                if (account !== null) {
                    const accountInfo = {};

                    accountInfo.accountNumber = account.accountNumber;
                    accountInfo.accountName = account.accountHolder;
                    accountInfo.isPerson = account.isPersonalAccount;
                    accountInfo.isCommercial = account.isCommercialAccount;
                    accountInfo.phoneNumber = getPhoneNumber(account);
                    accountInfo.policyCount = account.policySummaries.length;
                    accountInfo.accountHolderAddress = account.accountHolderAddress;
                    accounts.push(accountInfo);
                }
            });
        }

        return accounts;
    };
    
    const toBasePolicy = (policy) => {
        const policyInfo = {};

        policyInfo.policyNumber = policy.policyNumber;
        policyInfo.accountNumber = policy.accountNumber;

        if (policy.accountHolder.subtype === 'Company') {
            policyInfo.accountName = policy.accountHolder.contactName;
        } else if (policy.accountHolder.subtype === 'Person') {
            policyInfo.accountName = `${policy.accountHolder.firstName} ${policy.accountHolder.lastName}`;
        }

        policyInfo.product = policy.product;
        policyInfo.policyStatus = policy.jobStatus;
        policyInfo.phoneNumber = getPhoneNumber(policy.accountHolder);
        policyInfo.displayStatus = policy.displayStatus;
        policyInfo.effectiveTermNumber = policy.effectiveTermNumber;
        policyInfo.primaryAddress = policy.accountHolder.primaryAddress;
        policyInfo.policyLocations = policy?.policyLocations;

        return policyInfo;
    };

    const populatePolicyResults = (data) => {

        if (data.submissions.length === 0 && data.policies.length === 1 && data.accounts.length === 0) {
            history.push(
                `/policies/${data.policies[0].policyNumber}/${data.policies[0].effectiveTermNumber}/summary`
            );
        }

        return data.policies
                .filter(policies => policies.policyNumber)
                .map(policies => toBasePolicy(policies));

    };

    const toBaseSubmission = (submission) => {
        const submissionInfo = {};

        submissionInfo.rank = submission.rank_WMIC;
        submissionInfo.product = submission.product;
        submissionInfo.jobNumber = submission.jobNumber;
        submissionInfo.jobStatus = submission.jobStatus;
        submissionInfo.accountNumber = submission.accountNumber;
        submissionInfo.jobType = submission.jobType;
        submissionInfo.displayStatus = submission.displayStatus;

        if (submission.accountHolder.subtype === 'Company') {
            submissionInfo.accountName = submission.accountHolder.contactName;
        } else if (submission.accountHolder.subtype === 'Person') {
            submissionInfo.accountName = `${submission.accountHolder.firstName} ${submission.accountHolder.lastName}`;
        }
        
        submissionInfo.phoneNumber = getPhoneNumber(submission.accountHolder);
        submissionInfo.primaryAddress = submission.accountHolder.primaryAddress;
        submissionInfo.policyLocations = submission?.policyLocations;

        return submissionInfo;
    };

    const getSubmissionRedirectPath = (submission) => {
        switch (submission.jobType) {
            case JobType.POLICY_CHANGE: 
                return `/change/${submission.jobNumber}/summary`
            case JobType.RENEWAL:
                return `/renewal/${submission.jobNumber}/summary`
            default:
                return `/quotes/${submission.jobNumber}/summary`
        }
    }

    const populateSubmissionResults = (data) => {

        if (data.submissions.length === 1 && data.policies.length === 0 && data.accounts.length === 0) {
            history.push(getSubmissionRedirectPath(data.submissions[0]));            
        }

        return data.submissions
                .filter(submission => submission.jobNumber)
                .map(submission => toBaseSubmission(submission));

    };

    useEffect(() => {
        const searchParam = param || searchText;
        const fetchData = async () => {
            setLoading(true);
            setPrevParam(searchParam);

            const searchObj = {
                searchParam
            };
            const data = await SearchService.search(searchObj, authHeader);

            setLoading(false);
            setSearchResult(() => {
                return {
                    accounts: populateAccountResults(data),
                    policies: populatePolicyResults(data),
                    submissions: populateSubmissionResults(data),
                    isSOLRDisabled: data.isSOLRDisabled
                };
            });
        };

        if (searchParam && prevParam !== searchParam) {
            fetchData();            
        }
        
    }, [searchText, populateAccountResults, populatePolicyResults, populateSubmissionResults, authHeader, param, prevParam]);

    const getAddressOthers = ({city, state, postalCode}) => {
        return (
            <span>
                {`${city}, ${state} `}
                <span className='sr-only'>{`${WMICAddressDetailsUtil.getPostalCodeForScreenReader(postalCode)},`}</span>
                <span aria-hidden='true'>{postalCode}</span>
            </span>
        );
    }

    const accountDetailsOverrides = useMemo(() => {
        const overrides = searchResult.accounts.map((account, index) => {
            const iconType = getAccountIcon(account)

            return {
                [`accountLinkContainer${index}`]: {
                    to: `/accounts/${account.accountNumber}/summary`,
                    'aria-labelledby': `accountLinkText${index} accountAccountName${index} accountPhone${index} accountAddressLine${index} accountAddressOthers${index} accountPolicyCount${index}`
                },
                [`accountLinkIcon${index}`]: {
                    icon: iconType,
                    className: `goto linkIcon`
                },
                [`accountLinkText${index}`]: {
                    content: ` ${account.accountNumber}`
                },
                [`accountAccountName${index}`]: {
                    content: `${account.accountName}`
                },
                [`accountPhone${index}`]: {
                    value: `${account.phoneNumber}`,
                    visible: account.phoneNumber !== undefined
                },
                [`accountAddressLine${index}`]: {
                    content: getAddressLine(account.accountHolderAddress)
                },
                [`accountAddressOthers${index}`]: {
                    content: getAddressOthers(account.accountHolderAddress)
                },
                [`accountPolicyCount${index}`]: {
                    content: translator(messages.accountPolicyCount, {policyResultsNumber: account.policyCount})
                }
            };
        });

        return Object.assign({}, ...overrides);
    }, [searchResult.accounts, getAccountIcon]);

    const isPUP = (value) => value === LOBConstants.PUP

    const getAdditionalAddressesTooltip = (addresses) => {

        const addressList = (
            <ul className={styles.additionalAddressList}>
                {addresses.map(address => (
                    <li>
                        <span>{`${getAddressLine(address)} ${address.city}, ${address.state} `}
                            <span className='sr-only'>{WMICAddressDetailsUtil.getPostalCodeForScreenReader(address.postalCode)}</span>
                            <span aria-hidden='true'>{address.postalCode}</span>
                        </span>
                    </li>
                ))}
            </ul>
        );

        return (
            <Tooltip
                id='tooltip'
                content={addressList}
                trigger='mouseenter focus'>
                <div className={styles.viewAdditionalAddressesTextContainer} tabindex={0}>
                    <Icon icon="gw-info" id="icon" size="medium" className={styles.additionalAddressesIcon}></Icon>
                    <span>{translator(messages.viewAdditionalAddresses)}</span>
                </div>
            </Tooltip>
        );
    };

    const policyDetailsOverrides = useMemo(() => {
        const overrides = searchResult.policies.map((policy, index) => {
            const iconType = WMICVariousUtil.getLobIconForProduct(policy?.product);

            return {
                [`policyLinkContainer${index}`]: {
                    to: `/policies/${policy.policyNumber}/${policy.effectiveTermNumber}/summary`,
                    'aria-labelledby': `policyLinkText${index} policyAccountName${index} policyAddressLine${index} policyAddressOthers${index} policyPhone${index} policyStatus${index}`
                },
                [`policyLinkIcon${index}`]: {
                    icon: iconType,
                    className: `goto linkIcon ${(isPUP(policy?.product?.productCode)) ? ICON_NAMES.ICON_UMBRELLA : ""}`
                },
                [`policyLinkText${index}`]: {
                    content: ` ${policy.policyNumber}`
                },
                [`policyAdditionalAddressesTooltipIcon${index}`]: {
                    content: getAdditionalAddressesTooltip(policy.policyLocations),
                    visible: policy.policyLocations && policy.policyLocations.length > 0
                },
                [`policyAccountName${index}`]: {
                    content: `${policy.accountName}`
                },
                [`policyAddressLine${index}`]: {
                    content: getAddressLine(policy.primaryAddress)
                },
                [`policyAddressOthers${index}`]: {
                    content: getAddressOthers(policy.primaryAddress)
                },
                [`policyPhone${index}`]: {
                    value: `${policy.phoneNumber}`,
                    visible: policy.phoneNumber !== undefined
                },
                [`policyStatus${index}`]: {
                    content: translator(messages.resultStatus, {resultStatus: policy.displayStatus})
                }
            };
        });

        return Object.assign({}, ...overrides);
    }, [searchResult.policies]);

    const submissionDetailsOverrides = useMemo(() => {
        const overrides = searchResult.submissions.map((submission, index) => {
            const product = submission?.product;
            const icon = WMICVariousUtil.getLobIconForProduct(product);

            return {
                [`submissionLinkContainer${index}`]: {
                    to: getSubmissionRedirectPath(submission),
                    'aria-labelledby': `submissionLinkText${index} submissionAccountName${index} submissionAddressLine${index} submissionAddressOthers${index} submissionPhone${index} submissionStatus${index}`
                },
                [`submissionLinkIcon${index}`]: {
                    icon,
                    className: `goto linkIcon ${(isPUP(product?.productCode)) ? ICON_NAMES.ICON_UMBRELLA : ""}`
                },
                [`submissionLinkText${index}`]: {
                    content: `${submission.jobNumber}`
                },
                [`submissionAdditionalAddressesTooltipIcon${index}`]: {
                    content: getAdditionalAddressesTooltip(submission.policyLocations),
                    visible: submission.policyLocations && submission.policyLocations.length > 0
                },
                [`submissionAccountName${index}`]: {
                    content: `${submission.accountName}`
                },
                [`submissionAddressLine${index}`]: {
                    content: getAddressLine(submission.primaryAddress)
                },
                [`submissionAddressOthers${index}`]: {
                    content: getAddressOthers(submission.primaryAddress)
                },
                [`submissionPhone${index}`]: {
                    value: `${submission.phoneNumber}`,
                    visible: submission.phoneNumber !== undefined
                },
                [`submissionStatus${index}`]: {
                    content: translator(messages.resultStatus, {resultStatus: submission.displayStatus})
                }
            };
        });

        return Object.assign({}, ...overrides);
    }, [searchResult.submissions]);
    
    const searchResultsTitle = searchText ? translator(messages.searchResultsFor, { searchText }) : translator(messages.searchResults); 
    const hasSearchResults = searchResult.accounts.length > 0 || searchResult.policies.length > 0 || searchResult.submissions.length > 0

    const overrideProps = {
        accountResults: {
            visible: searchResult.accounts.length > 0 && !loading
        },
        searchResultsTitle: {
            content: title || searchResultsTitle
        },
        policyResults: {
            visible: searchResult.policies.length > 0 && !loading
        },
        submissionResults: {
            visible: searchResult.submissions.length > 0 && !loading
        },
        searchingText: {
            visible: loading
        },
        loadingIndicator: {
            loaded: !loading
        },
        noResultsMessage: {
            visible:
                (!!searchText || param)
                && !loading
                && !searchResult.accounts.length
                && !searchResult.policies.length
                && !searchResult.submissions.length,
            content:
                noResultsMessage
                || translator(messages.noResultsFoundFor, {
                    query: searchText || param
                })
        },
        accountHeader: {
            content: translator(
                messages.accountResults,
                { accountResultsNumber: searchResult.accounts.length }
            )
        },
        statusComponentContainer: {
            visible: !loading && !!searchResult.isSOLRDisabled
        },
        statusMessageBody: {
            content: WMICRichTextUtil.translateRichText(translator(messages.solrDisabledMessage))
        },
        tooManyResultsText: {
            visible: !searchResult.isSOLRDisabled && hasSearchResults,
            content: WMICRichTextUtil.translateRichText(translator(messages.tooManyResults))
        },
        policyHeader: {
            content: translator(
                messages.policyResults,
                { policyResultsNumber: searchResult.policies.length }
            )
        },
        submissionHeader: {
            content: translator(
                messages.submissionResults,
                { submissionResultsNumber: searchResult.submissions.length }
            )
        },
        ...accountDetailsOverrides,
        ...policyDetailsOverrides,
        ...submissionDetailsOverrides
    };

    const resolvers = {
        resolveClassNameMap: styles,
    };

    const readValue = useCallback(
        (id, path) =>  readViewModelValue(metadata.pageContent, searchResult, id, path, overrideProps),
        [overrideProps, searchResult]
    );

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={searchResult}
            overrideProps={overrideProps}
            classNameMap={resolvers.resolveClassNameMap}
            resolveValue={readValue}
        />
    );
}

SearchResults.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            searchText: PropTypes.string
        })
    }).isRequired,
    param: PropTypes.string,
    title: PropTypes.string,
    noResultsMessage: PropTypes.string
};

SearchResults.defaultProps = {
    param: '',
    title: '',
    noResultsMessage: ''
};

export default withRouter(SearchResults);
